import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DocumentType } from '../../models/DocumentType'

import { RootState } from '../store'

interface DocumentSummaryResult {
  summary: string
}

interface DocumentSummaryState {
  result: DocumentSummaryResult | null
  executeIsLoading: boolean
  interactionIsLoading: boolean
  aiAgentId: string | null
  isEditing: boolean
  documentTypeCalculationIsLoading: boolean
  documentType: DocumentType | null
  documentLabel: string | null
  showDocumentSummary: boolean
  documentIsNotRelatedToClient: boolean
  selectedDocumentType: DocumentType | null
}

const initialState: DocumentSummaryState = {
  result: null,
  interactionIsLoading: false,
  executeIsLoading: false,
  aiAgentId: null,
  isEditing: false,
  documentTypeCalculationIsLoading: false,
  documentType: null,
  documentLabel: null,
  showDocumentSummary: false,
  documentIsNotRelatedToClient: false,
  selectedDocumentType: null,
}

export const documentSummarySlice = createSlice({
  name: 'documentSummary',
  initialState,
  reducers: {
    setDocumentSummaryResult: (state, action: PayloadAction<DocumentSummaryResult>) => {
      state.result = { ...state.result, ...action.payload }
    },
    clearDocumentSummaryResult: (state) => {
      state.result = null
    },
    setDocumentSummaryExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executeIsLoading = action.payload
    },
    setDocumentSummaryInteractionLoading: (state, action: PayloadAction<boolean>) => {
      state.interactionIsLoading = action.payload
    },
    setDocumentSummaryAiAgentId: (state, action: PayloadAction<string>) => {
      state.aiAgentId = action.payload
    },
    setDocumentSummaryEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload
    },
    setDocumentTypeCalculationLoading: (state, action: PayloadAction<boolean>) => {
      state.documentTypeCalculationIsLoading = action.payload
    },
    setDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.documentType = action.payload
    },
    setDocumentLabel: (state, action: PayloadAction<string>) => {
      state.documentLabel = action.payload
    },
    setShowDocumentSummary: (state, action: PayloadAction<boolean>) => {
      state.showDocumentSummary = action.payload
    },
    setDocumentIsNotRelatedToClient: (state, action: PayloadAction<boolean>) => {
      state.documentIsNotRelatedToClient = action.payload
    },
    setSelectedDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.selectedDocumentType = action.payload
    },
    resetDocumentSummary: () => initialState,
  },
})

export const {
  setDocumentSummaryResult,
  clearDocumentSummaryResult,
  setDocumentSummaryExecutionLoading,
  setDocumentSummaryInteractionLoading,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryEditing,
  setDocumentTypeCalculationLoading,
  setDocumentType,
  setDocumentLabel,
  setShowDocumentSummary,
  setDocumentIsNotRelatedToClient,
  setSelectedDocumentType,
  resetDocumentSummary,
} = documentSummarySlice.actions

export const selectDocumentSummaryResult = (state: RootState) => state.processDocument.documentSummary.result

export const selectDocumentSummaryExecutionLoading = (state: RootState) => state.processDocument.documentSummary.executeIsLoading

export const selectDocumentSummaryInteractionLoading = (state: RootState) => state.processDocument.documentSummary.interactionIsLoading

export const selectDocumentSummaryAiAgentId = (state: RootState) => state.processDocument.documentSummary.aiAgentId

export const selectDocumentSummaryEditing = (state: RootState) => state.processDocument.documentSummary.isEditing

export const selectDocumentTypeCalculationLoading = (state: RootState) => state.processDocument.documentSummary.documentTypeCalculationIsLoading

export const selectDocumentType = (state: RootState) => state.processDocument.documentSummary.documentType

export const selectDocumentLabel = (state: RootState) => state.processDocument.documentSummary.documentLabel

export const selectShowDocumentSummary = (state: RootState) => state.processDocument.documentSummary.showDocumentSummary

export const selectDocumentIsNotRelatedToClient = (state: RootState) => state.processDocument.documentSummary.documentIsNotRelatedToClient

export const selectSelectedDocumentType = (state: RootState) => state.processDocument.documentSummary.selectedDocumentType
